import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Layout from '../../components/Layout';
import api from '../../config/api';
import ReCAPTCHA from "react-google-recaptcha-enterprise";
import EyeIcon from '../../components/Icons/EyeIcon';
import EyeSlashIcon from '../../components/Icons/EyeSlashIcon';

import {
  Container,
  Input,
  InputContainer,
  InputPasswordContainer,
  EyeButton,
  InputLabel,
  Button,
  Form,
  ErrorText,
  Header,
  Description,
  WarnContainer,
  WarnBody,
  BoldText,
  LinkButton
} from './styles';

const Login = () => {
  const [captchaToken, setCaptchaToken] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const { setAuthData, token, isFirstLogin } = useAuth();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (token) {
      // If it's the user's first login, navigate to change-password
      if (Number(isFirstLogin)) {
        navigate("/change-password", { replace: true, state: { isFirstLogin } });
      } else {
        navigate("/chatbot", { replace: true });
      }
    }
  }, [token, navigate, isFirstLogin]);

  useEffect(() => {
    setErrorMessage('');
  }, [password, username]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setErrorMessage("Please confirm you're not a robot");
      return;
    }

    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        { username, password, captchaToken }
      );
      
      if (response.status === 200) {
        const user = response.data;
        setAuthData(user.token, user.user_id, user.username, user.is_first_login);
      } else {
        setErrorMessage('Invalid username or password');
        setCaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }
    } catch (error) {
      console.error('Error during login:', error);
      if (error.status === 401) {
        setErrorMessage(error.response?.data?.message || 'Invalid username or password');
        setCaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }
    }
  };

  return (
    <Layout>
      <Container>
        <Header>Sign in to SH+ Assistant</Header>
        <Form onSubmit={handleLogin}>
          <WarnContainer>
            <WarnBody><BoldText>Important:</BoldText> SH+ Assistant and SH+ have <BoldText>separate passwords</BoldText>.</WarnBody>
            <WarnBody>If this is your <BoldText>first time</BoldText> logging in to SH+ Assistant, you should have a temporary password. After logging in, you’ll be prompted to set a new password.</WarnBody>
          </WarnContainer>
          <Description>Insert your username and password below to access your secure SH+ Assistant</Description>
          <InputLabel>Username</InputLabel>
          <InputContainer>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
            />
          </InputContainer>
          <InputLabel>Password</InputLabel>
          <InputPasswordContainer>
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
            />
            <EyeButton
              onClick={() => setShowPassword(!showPassword)}
              type="button"
            >
              {showPassword ? <EyeSlashIcon size={20} /> : <EyeIcon size={20} />}
            </EyeButton>
          </InputPasswordContainer>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
            onChange={setCaptchaToken}
            action="LOGIN"
          />
          <LinkButton to="/forgotten-password">Forgot Password?</LinkButton>
          {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
          <Button disabled={!username || !password} type="submit">Sign in</Button>
        </Form>
      </Container>
    </Layout>
  );
};

export default Login;
